import { defineComponent, getCurrentInstance, ref } from 'vue';
import { Toast } from '@cfx/base-mobile';
import { sendData } from '@cfx/send-chat-message';
import type { IMsgtype, ITypeMap } from '@cfx/send-chat-message';
import { createMaterialUrl } from '@markting/mk-create-h5-url';
import styles from './index.module.scss';
import { getBindMiniAppId } from '@/model/commonApi';
import { reportMaterial } from '@/hooks/useReport';

export default defineComponent({
  name: 'MkFooterSend',
  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const globalProperties = getCurrentInstance()?.appContext?.config?.globalProperties as Record<string, any>;
    const isBindMini = ref(true);

    const { $isSideMenu, $wxVector } = globalProperties;

    // 是否为移动端
    const isMobile = /(iPhone|iPod|iPad|Android|ios|SymbianOS|Mobile)/i.test(navigator.userAgent);

    const registerSDK = async () => {
      // 从移动端侧边栏进入时需注册企微sdk $wxVector: 新版侧边栏，不需要注册企微sdk 复用父级的window.wx
      if (isMobile && $isSideMenu && !$wxVector) {
        Toast.loading({
          message: '正在加载中',
          duration: 0,
          forbidClick: true
        });
        await globalProperties.$wxSdk().init();
        Toast.clear();
      }
    };

    registerSDK();

    // 判断是否绑定了名片小程序
    const getIsBindMini = async () => {
      try {
        const appId = await getBindMiniAppId();
        isBindMini.value = !!appId;
      }
      catch (error) {
        isBindMini.value = false;
      }
    };

    getIsBindMini();

    /**
     *  获取素材预览地址
     * @param id 素材ID
     * @param sendType 消息格式
     * @returns
     */
    const getMaterialUrl = async (id: string, sendType = 'news') => {
      const { $urlObj, $uuid } = globalProperties;
      const saleShareId = $uuid();

      // 当为移动端时，表明是在C端访问，所以需要重新处理 user 数据，然后传入进去
      // 当为预览时，表明包含了iframe查看，则也需要重新处理 user 数据，然后传入进去
      const userInfo: any = {
        id: $urlObj.userid,
        account: $urlObj.account,
        corpId: $urlObj.corpId,
        uid: $urlObj.qwUid,
        qwUserId: $urlObj.qwUserId,
        agentId: $urlObj.agentId,
        suiteId: $urlObj.suiteId
      };

      const otherParams: any = {
        saleShareId,
        noCorpIdInUrl: !!(sendType === 'miniprogram'),
        projectName: 'qwScrm',
        // 以下参数主要用于触达上报
        deliverType: 'staffShare',
        shareId: $urlObj.qwUid,
        shareType: 'staff'
      };

      const sceneType = sendType === 'news' ? 'sidebar-H5' : 'sidebar-small-routine';

      const { linkUrl, adId } = await createMaterialUrl({ id }, $urlObj.materialType, 'qw', true, otherParams, userInfo, sceneType);

      return { linkUrl, saleShareId, adId };
    };

    /**
     * 根据定义的类型构造发送
     * @param msgType
     * @param sendParams
     * @returns
     */
    const multitypeSend = async <T extends IMsgtype>(msgType: T, sendParams: ITypeMap[T], reportData: any) => {
      Toast.loading({ message: '发送中', duration: 0, forbidClick: true });
      let res = await sendData(msgType, sendParams, false, 15000, $wxVector);
      Toast.clear();

      // JSSDK注册失败则重新注册
      if (res.code === 100001) {
        await globalProperties.$wxSdk().init();
        res = await sendData(msgType, sendParams);
      }

      // 取消发送
      if (res.code === 500201) {
        return res;
      }

      if (!res?.success) {
        const failTips = [100002, 200002, 200003].includes(res?.code) ? res.msg : '发送失败';
        Toast.fail(failTips);
        return res;
      }

      Toast.success('发送成功');

      // 销售分享行为 + 发送次数 + 触达上报
      const { id, type, saleShareId, adId } = reportData;
      const { $urlObj } = globalProperties;
      reportMaterial({ id, type, saleShareId, adId, account: $urlObj.account, uid: $urlObj.qwUid });
    };

    /**
     * 格式化参数 & 执行发送动作
     * @param sendType
     */
    const onSend = async (sendType: IMsgtype = 'news') => {
      if (props.item.status === 2) {
        Toast('发送失败，当前素材已下架');
        return;
      }
      let sendParams: any = {};
      const { $urlObj } = globalProperties;
      const type = $urlObj.materialType;
      const { id } = props.item;
      let { cover, desc, title } = props.item;

      if (type === 'form') {
        cover = props.item.shareConfig.img || cover;
        desc = props.item.shareConfig.desc || desc;
        title = props.item.shareConfig.title || title;
      }
      const { linkUrl, saleShareId, adId } = await getMaterialUrl(id, sendType);

      if (sendType === 'news') {
        sendParams = {
          link: linkUrl,
          title,
          desc: desc || '点击查看',
          imgUrl: globalProperties.$ProcessBase.processImage(cover, { resize: { w: 188 } }) // H5消息封面图片URL
        };
      }

      if (sendType === 'miniprogram') {
        try {
          // 判断是否绑定小程序，获取小程序appid
          const appid = await getBindMiniAppId();
          const path = `pages/material-details/index.html?scene=${encodeURIComponent(linkUrl)}&saleShareId=${saleShareId}&projectName=qwScrm&secondScene=0&title=${encodeURIComponent(title)}&type=${props.item.type}`;
          sendParams = {
            appid, // H5消息标题
            title, // H5消息封面图片URL
            imgUrl: globalProperties.$ProcessBase.processImage(cover, { resize: { w: 400 } }),
            page: path // H5消息页面url 必填
          };
        }
        catch (err) {
          Toast('生成小程序素材请联系管理员完成小程序授权');
        }
      }

      multitypeSend(sendType, sendParams, { id, type, saleShareId, adId });
    };

    return () => (
      <>
        {
          ((props.item.shareType?.includes(2) && isBindMini.value) || props.item.shareType?.includes(1)) ? (
            <div class={`${styles['mk-footer-send']} mk-bg-white flex-c`}>
              {
                (props.item.shareType?.includes(2) && isBindMini.value) ? (
                  <button class={'flex-1 mk-border-primary mk-bg-white mk-text-primary mk-fs-16'} onClick={() => onSend('miniprogram')}>
                    <duero-iconfont name="duero-icon-3181568-fasong" />
                    <span style={'margin-left:4px;'}>小程序</span>
                  </button>
                ) : ''
              }
              {
                props.item.shareType?.includes(1) ? (
                  <button class={'flex-1 mk-bg-primary mk-text-white mk-fs-16'} onClick={() => onSend('news')}>
                    <duero-iconfont name="duero-icon-3181568-fasong" />
                    <span style={'margin-left:4px;'}>H5发送</span>
                  </button>
                ) : ''
              }
            </div>
          ) : ''
        }
      </>
    );
  }
});
